import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import {
  AccordionDefault,
  AccordionItem,
} from '../accordion-default/accordion-default';

@Component({
  selector: 'app-layout-cindi-movia',
  imports: [MatExpansionModule, AccordionDefault],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './layout-cindi-movia.html',
  styleUrl: './layout-cindi-movia.scss',
})
export class LayoutCindiMovia implements OnInit {
  @Input() detalhesTriagem: any = null;
  readonly panelOpenState = signal(false);
  triagemPreenchida = signal(false);

  ngOnInit(): void {
    this.setTriagemInicial();
    this.setBiopsicossociais();
    this.setAvaliacaoDaDor();
    this.setFinalizacao();
    this.atualizaStatusTriagemPreenchida();
  }

  atualizaStatusTriagemPreenchida() {
    return [
      ...this.triagemInicial,
      ...this.bioPsicossocial,
      ...this.avaliacaoDaDor,
      ...this.finalizacao,
    ].filter((item: AccordionItem) => {
      if (!!item.content) {
        this.triagemPreenchida.set(true);
        return;
      }
    });
  }

  triagemInicial: AccordionItem[] = [];
  setTriagemInicial() {
    const t = this.detalhesTriagem.triagemInicial;

    this.triagemInicial = [
      {
        title: 'Sente dor ou desconforto na região lombar',
        content: t.senteDor ?? '',
      },
      {
        title: 'Há quanto tempo você sente dor na lombar?',
        content: t.tempoDor ?? '',
      },
      {
        title:
          'Você acha que essa dor pode ter relação com alguma outra condição, lesão ou doença?',
        content: t.relacaoComOutraCondicao ?? '',
      },
      {
        title: 'Você se identifica com alguma das situações abaixo?',
        content: t.identificaSituacoes ?? '',
      },
    ];
  }

  bioPsicossocial: AccordionItem[] = [];
  setBiopsicossociais() {
    const b = this.detalhesTriagem.biopsicossociais;
    this.bioPsicossocial = [
      {
        title: 'Em qual país você nasceu?',
        content: b.pais ?? '',
      },
      {
        title: 'Em qual cidade você reside atualmente?',
        content: b.cidade ?? '',
      },
      {
        title: 'Qual é o seu estado civil?',
        content: b.estadoCivil ?? '',
      },
      {
        title: 'Qual o seu grau de escolaridade?',
        content: b.escolaridade ?? '',
      },
      {
        title: 'Como você se identifica no quesito étnico-racial?',
        content: b.etnicoSocial ?? '',
      },
      {
        title: `Qual o seu cargo na ${this.detalhesTriagem.nomeEmpresa}?`,
        content: b.cargo ?? '',
      },
      {
        title: `Qual o local que você atua aqui na ${this.detalhesTriagem.nomeEmpresa}?`,
        content: b.localQueAtua ?? '',
      },
      {
        title: `Em qual turno você trabalha na ${this.detalhesTriagem.nomeEmpresa}?`,
        content: Array.isArray(b.turno) ? b.turno.join(', ') : (b.turno ?? ''),
      },
    ];
  }

  avaliacaoDaDor: AccordionItem[] = [];
  setAvaliacaoDaDor() {
    const d = this.detalhesTriagem.dor;

    this.avaliacaoDaDor = [
      {
        title:
          'Escala de dor - 0 é sem dor e 10 é a pior dor que você pode imaginar',
        content: String(d.escala ?? ''),
      },
      {
        title:
          'Você sentiu que a dor na lombar se espalhou para as pernas nas últimas duas semanas?',
        content: d.dorSeEspalhou ?? '',
      },
      {
        title:
          'Você sentiu dor no ombro e/ou na nuca pelo menos uma vez nas últimas duas semanas?',
        content: d.dorNoOmbro ?? '',
      },
      {
        title: 'Você evita andar longas distâncias por causa da dor na lombar?',
        content: d.evitaLongasDistancias ?? '',
      },
      {
        title:
          'Você tem se vestido mais devagar por causa da dor na lombar nas últimas duas semanas?',
        content: d.vestidoMaisDevagar ?? '',
      },
      {
        title:
          'Você concorda que atividade física não é segura para uma pessoa com um problema como o seu?',
        content: d.atividadeFisicaNaoSegura ?? '',
      },
      {
        title:
          'Você tem ficado preocupado por muito tempo por causa da dor na lombar?',
        content: d.preocupacaoComLombar ?? '',
      },
      {
        title:
          'Você sente que a dor na lombar é terrível e que nunca vai melhorar?',
        content: d.dorTerrivel ?? '',
      },
      {
        title:
          'Você percebe que não tem gostado de todas as coisas como costumava gostar?',
        content: d.perdaDeInteresse ?? '',
      },
      {
        title:
          'Na sua percepção, quanto a sua dor o incomodou nas duas últimas semanas?',
        content: d.nivelDeIncomodo ?? '',
      },
      {
        title:
          'Você sente que o seu trabalho pode ter contribuído para o surgimento ou agravamento da sua dor lombar?',
        content: String(d.contribuicaoTrabalho ?? ''),
      },
      {
        title:
          'Você sente que a sua dor nas costas tem dificultado ou impedido que você realize suas atividades de trabalho normalmente?',
        content: String(d.impactoNoTrabalho ?? ''),
      },
      {
        title:
          'Você já faz algum tipo de tratamento e/ou utiliza alguma estratégia para lidar com a dor lombar?',
        content: `${d.tratamentosAnteriores?.selecionados ?? ''} ${
          d.tratamentosAnteriores?.textoOutros ?? ''
        }`,
      },
      {
        title:
          'O tratamento ou as estratégias que você utiliza têm ajudado a aliviar a sua dor lombar?',
        content: d.tratamentoEstaAjudando ?? '',
      },
    ];
  }

  finalizacao: AccordionItem[] = [];
  setFinalizacao() {
    const f = this.detalhesTriagem.finalizacao;

    this.finalizacao = [
      {
        title:
          'Tem mais alguma questão relacionada à sua saúde que está te incomodando ou preocupando?',
        content: f.possuiOutraPreocupacao ?? '',
      },
      {
        title:
          'Se você se sentir à vontade, gostaria que me contasse um pouco mais sobre o que tem te preocupado.',
        content: f.descricaoPreocupacao ?? '',
      },
      {
        title: 'Pesquisa de satisfação',
        content: String(f.satisfacao ?? 0),
      },
    ];
  }
}
